import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { History, TriangleAlert } from 'lucide-react';
import Ics204VersionsDialog from '../planningView/widgets/AssignmentList/Ics204VersionsDialog';
import Ics204VersionStatusBar from '../planningView/widgets/AssignmentList/Ics204VersionStatusBar';
import type { WalaSignature } from '../planningView/widgets/AssignmentList/ics204SignatureSlots';
import { getTaskExecutionApprovalDrift } from './taskApprovalDrift';
import {
  canRejectCompletion,
  canRejectExecution,
  isApproveExecuteCtaKind,
  getTaskSignatureCompletion,
  isTaskFullySigned,
  nextTaskApprovalCta,
} from './taskSignatureSlots';

export type TaskVersionRow = {
  id: string;
  version: number;
  timestamp: string | Date;
  user_name?: string | null;
  status?: string | null;
  signatures?: unknown;
};

function toIso(at: string | Date): string {
  return typeof at === 'string' ? at : new Date(at).toISOString();
}

function formatApprovedAt(signedAt: number): string {
  const when = new Date(signedAt);
  if (Number.isNaN(when.getTime())) return 'unknown time';
  return when.toLocaleString();
}

export function TaskVersionBanner({
  versions,
  viewing,
  status,
  signatures,
  saving,
  onView,
  onViewLatest,
  onApprovalCta,
  onRejectExecution,
  onRejectCompletion,
  onDraftNewVersion,
}: {
  versions: TaskVersionRow[];
  viewing: TaskVersionRow | null;
  status: string | null | undefined;
  signatures: WalaSignature[];
  saving: boolean;
  onView: (row: TaskVersionRow) => void;
  onViewLatest: () => void;
  /** Opens the route, sign, begin-execution, or approve-execution dialog. */
  onApprovalCta: () => void;
  /** Opens Reject Execution (while either Approve Execution CTA is showing). */
  onRejectExecution?: () => void;
  /** Opens Reject Completion (only while Approve Completion is the CTA). */
  onRejectCompletion?: () => void;
  /** After fully signed: new Planned tip with cleared signatures. */
  onDraftNewVersion?: () => void;
}) {
  const [historyOpen, setHistoryOpen] = useState(false);
  const tip = versions[0];
  const shown = viewing ?? tip;
  const when = shown?.timestamp;
  const past = Boolean(viewing);
  const fullySigned = isTaskFullySigned(signatures);
  const completion = getTaskSignatureCompletion(signatures);
  const cta = nextTaskApprovalCta(signatures, status);
  const updatedAt = when ? toIso(when) : null;
  const updatedByUserName = fullySigned
    ? `${shown?.user_name?.trim() || 'Unknown'} · Fully signed`
    : shown?.user_name;

  const tipForDrift = tip
    ? {
        ...tip,
        // Live tip stamps may be ahead of the versions query until refetch.
        signatures: past ? tip.signatures : signatures,
      }
    : null;
  const drift =
    !past && tipForDrift
      ? getTaskExecutionApprovalDrift(tipForDrift, versions)
      : null;

  const openHistory = () => setHistoryOpen(true);

  const showRejectExecution =
    !past &&
    !!onRejectExecution &&
    isApproveExecuteCtaKind(cta?.kind) &&
    canRejectExecution(signatures);

  const showRejectCompletion =
    !past &&
    !!onRejectCompletion &&
    cta?.kind === 'approve-completion' &&
    canRejectCompletion(signatures);

  const approvalButton =
    cta && !past ? (
      <Button
        type="button"
        size="sm"
        className="tw:h-7 tw:text-xs"
        disabled={saving}
        onClick={onApprovalCta}
      >
        {cta.label}
      </Button>
    ) : null;

  const rejectExecutionButton = showRejectExecution ? (
    <Button
      type="button"
      size="sm"
      variant="destructive"
      className="tw:h-7 tw:text-xs"
      disabled={saving}
      onClick={onRejectExecution}
    >
      Reject Execution
    </Button>
  ) : null;

  const rejectCompletionButton = showRejectCompletion ? (
    <Button
      type="button"
      size="sm"
      variant="destructive"
      className="tw:h-7 tw:text-xs"
      disabled={saving}
      onClick={onRejectCompletion}
    >
      Reject Completion
    </Button>
  ) : null;

  /** Past snapshot: status bar omits the Versions menu, so keep history reachable. */
  const pastVersionsButton = past ? (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="tw:h-7 tw:shrink-0 tw:gap-1 tw:text-xs"
      disabled={!versions.length}
      onClick={openHistory}
    >
      <History className="tw:h-3.5 tw:w-3.5" />
      Versions
      <Badge
        variant="secondary"
        className="tw:ml-1 tw:h-4 tw:px-1.5 tw:text-[10px]"
      >
        {versions.length}
      </Badge>
    </Button>
  ) : null;

  const driftWarning = drift ? (
    <TooltipProvider delay={200}>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              className="tw:inline-flex tw:shrink-0 tw:items-center tw:justify-center tw:rounded-sm tw:text-amber-300 tw:outline-none tw:focus-visible:ring-2 tw:focus-visible:ring-ring"
              aria-label="Task updated since Execution Approval"
              data-testid="task-execution-approval-drift"
            >
              <TriangleAlert className="tw:h-3.5 tw:w-3.5" />
            </button>
          }
        />
        <TooltipContent
          side="top"
          className="tw:flex tw:w-[18rem] tw:max-w-[18rem] tw:flex-col tw:items-stretch tw:gap-2 tw:whitespace-normal tw:p-3"
        >
          <p className="tw:m-0 tw:text-left tw:text-[11px] tw:leading-snug tw:text-background">
            This Task has been updated since it was approved by{' '}
            {drift.approverName} at {formatApprovedAt(drift.approvedAt)}.
          </p>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="tw:h-7 tw:w-full tw:shrink-0 tw:border-background/40 tw:bg-transparent tw:text-xs tw:text-background tw:hover:bg-background/15 tw:hover:text-background"
            onClick={() => {
              const row = versions.find(
                (item) => item.id === drift.approvedVersionId
              );
              if (row) onView(row);
            }}
          >
            View Approved Version
          </Button>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  ) : null;

  return (
    <>
      <Ics204VersionStatusBar
        signatures={signatures}
        viewingPast={past}
        isCreatingSignedVersion={false}
        historyCount={versions.length}
        signedCount={completion.completedCount}
        createItems={[]}
        updatedAt={updatedAt}
        updatedByUserName={updatedByUserName}
        onOpenHistory={openHistory}
        onOpenSigned={() => undefined}
        onViewLatest={onViewLatest}
        onCancelCreateSigned={() => undefined}
        onDraftNewVersion={
          fullySigned && !past && onDraftNewVersion
            ? onDraftNewVersion
            : undefined
        }
        draftNewVersionDisabled={saving}
        getCompletion={getTaskSignatureCompletion}
        hideProgressRing={false}
        hideLockedMessage
        hideSignedMenu
        progressTooltipTitle="Task Lifecycle"
        afterProgress={driftWarning}
        trailing={
          <>
            {pastVersionsButton}
            {approvalButton}
            {rejectExecutionButton}
            {rejectCompletionButton}
          </>
        }
      />
      <Ics204VersionsDialog
        open={historyOpen}
        kind="all"
        versions={versions.map((row) => ({
          id: row.id,
          updatedAt: toIso(row.timestamp),
          updatedByUserName: row.user_name,
          status: row.status,
        }))}
        tipId={tip?.id}
        description="Each save of this task is kept. Open one to view that snapshot."
        statusLabel={(version) => version.status || 'Planned'}
        onClose={() => setHistoryOpen(false)}
        onView={(version) => {
          const row = versions.find((item) => item.id === version.id);
          if (row) onView(row);
          setHistoryOpen(false);
        }}
      />
    </>
  );
}
